/**
 * Item Master Screen (4.1.2) — Procurement Module / Masters
 *
 * Classification tab is FULLY DYNAMIC — driven by Attribute Studio (useAttributeEngine).
 * Any attribute added/removed in Attribute Studio automatically appears here.
 *
 * Groups rendered:
 *   grp_classification → LookUpEdit dropdowns  (Division, Dept, Category, SubCat)
 *   grp_style          → Brand, Style, Design, Colour, Size + ComboBox seasons
 *   grp_physical       → Fabric Type, Fit, Collar Type, Wash Type, Style Detail (TextEdit)
 *   grp_pricing        → Price Info tab  (MRP, Cost Rate, Wholesale, WSP, Disc Type/Val)
 *   grp_compliance     → Stock/Compliance tab (UOM, HSN, Batch No, Expiry Date)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Search, Save, RefreshCw, AlertCircle, Plus, Edit2, Trash2, X, Image, Globe } from 'lucide-react';
import { useAttributeEngine } from '../../../hooks/useAttributeEngine';
import { useT, LanguageSwitcher } from '../../../lib/manchala-i18n.react.js';

// Groups that belong to the Classification tab
const CLASSIFICATION_GROUPS = ['grp_classification', 'grp_style', 'grp_physical'];
const GROUP_LABELS = {
  grp_classification: 'Classification',
  grp_style:          'Style & Fashion',
  grp_physical:       'Physical Details',
  grp_pricing:        'Pricing',
  grp_compliance:     'Compliance',
};

const ItemMasterScreen = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const t = useT();
  const [selectedIndustry, setSelectedIndustry] = useState(() => localStorage.getItem('ae_selected_industry') || 'Apparel');
  const [aeIndustries, setAeIndustries] = useState([]);
  const { byGroup, industryByGroup, industryGroups, toLookupOptions, getGroup, loading: aeLoading } = useAttributeEngine({ industryId: selectedIndustry });
  const handleIndustryChange = (ind) => { setSelectedIndustry(ind); localStorage.setItem('ae_selected_industry', ind); };

  // ── Theme helpers ───────────────────────────────────────────────────────
  const bgColor    = isDark ? 'bg-[#1a1a2e]' : 'bg-gray-100';
  const cardBg     = isDark ? 'bg-[#16213e]' : 'bg-white';
  const inputBg    = isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50';
  const borderColor = isDark ? 'border-[#1f4068]' : 'border-gray-300';
  const textColor  = isDark ? 'text-white' : 'text-gray-900';
  const labelColor = isDark ? 'text-gray-300' : 'text-gray-700';
  const sectionBg  = isDark ? 'bg-[#1f2937]' : 'bg-gray-50';
  const subHeadColor = isDark ? 'text-blue-400' : 'text-blue-700';

  // ── Form state ──────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    itemCode: 'IT10001', barcode: '', itemName: '', shortName: '', hsnCode: '',
    status: 'Active', allowDiscount: true,
    taxId: '', taxPercent: '0', hsnSacCode: '',
    uomId: '', reorderLevel: '0', minOrderQty: '0',
    costPrice: '0.00', mrp: '0.00', sellingPrice: '0.00', minSellingPrice: '0.00', marginPercent: '0.00',
  });
  // Dynamic attribute values keyed by AttributeId
  const [attrValues, setAttrValues] = useState({});

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic-info');

  const setField = (name, val) => setFormData(p => ({ ...p, [name]: val }));
  const setAttr = (attrId, val) => setAttrValues(p => ({ ...p, [attrId]: val }));

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      setData([
        { id: 1, itemCode: 'IT10001', itemName: 'MENS FORMAL SHIRT', hsnCode: '6205', division: 'MENS',   category: 'SHIRTS',   mrp: '2999.00', status: 'Active' },
        { id: 2, itemCode: 'IT10002', itemName: 'LADIES CASUAL TOP', hsnCode: '6206', division: 'LADIES', category: 'TOPS',     mrp: '1499.00', status: 'Active' },
        { id: 3, itemCode: 'IT10003', itemName: 'KIDS T-SHIRT',      hsnCode: '6109', division: 'KIDS',   category: 'T-SHIRTS', mrp: '799.00',  status: 'Active' },
      ]);
    } catch { setError('Failed to load data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Load industry list for the selector
  useEffect(() => {
    import('../../../lib/api').then(({ API_BASE, getToken }) => {
      fetch(`${API_BASE}/api/attribute-engine/industries`, { headers: { Authorization: `Bearer ${getToken()}` } })
        .then(r => r.json()).then(j => { if (j.success) setAeIndustries(j.data || []); }).catch(() => {});
    });
  }, []);

  const handleSave = () => {
    if (!formData.itemName) { setError(t('itemMaster.item_name', 'Item Name') + ' is required'); return; }
    console.log('Saving:', { ...formData, ...attrValues });
    alert('Saved successfully!');
  };

  const handleEdit = (item) => {
    setFormData(p => ({ ...p, itemCode: item.itemCode, itemName: item.itemName, hsnCode: item.hsnCode, status: item.status, mrp: item.mrp }));
    setAttr('division', item.division); setAttr('category', item.category);
    setActiveTab('basic-info');
  };

  const handleDelete = (item) => {
    if (window.confirm(`Delete "${item.itemName}"?`)) setData(data.filter(d => d.id !== item.id));
  };

  const resetForm = () => {
    setFormData({
      itemCode: `IT${10000 + data.length + 1}`, barcode: '', itemName: '', shortName: '', hsnCode: '',
      status: 'Active', allowDiscount: true, taxId: '', taxPercent: '0', hsnSacCode: '',
      uomId: '', reorderLevel: '0', minOrderQty: '0',
      costPrice: '0.00', mrp: '0.00', sellingPrice: '0.00', minSellingPrice: '0.00', marginPercent: '0.00',
    });
    setAttrValues({});
  };

  // ── Reusable field components ───────────────────────────────────────────

  const InputField = ({ label, name, type = 'text', required, disabled }) => (
    <div className="flex items-center mb-1.5">
      <label className={`w-32 text-xs ${labelColor} shrink-0 ${required ? 'after:content-["*"] after:text-red-500 after:ml-0.5' : ''}`}>{label}</label>
      <input type={type} value={formData[name] || ''} onChange={e => setField(name, e.target.value)} disabled={disabled}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor} ${disabled ? 'opacity-60' : ''}`}
        data-testid={`input-${name}`} />
    </div>
  );

  const StaticSelect = ({ label, name, options, required }) => (
    <div className="flex items-center mb-1.5">
      <label className={`w-32 text-xs ${labelColor} shrink-0 ${required ? 'after:content-["*"] after:text-red-500 after:ml-0.5' : ''}`}>{label}</label>
      <select value={formData[name] || ''} onChange={e => setField(name, e.target.value)}
        className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
        data-testid={`select-${name}`}>
        <option value="">--Select--</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  /** Dynamic field rendered from an AE Attribute definition */
  const AttrField = ({ attr }) => {
    const opts = toLookupOptions(attr.AttributeId);
    const val = attrValues[attr.AttributeId] || '';
    const isSelect = attr.ControlType === 'LookUpEdit' || attr.ControlType === 'ComboBoxEdit';
    const isDate   = attr.ControlType === 'DateEdit';
    const isNumber = attr.ControlType === 'CalcEdit' || attr.ControlType === 'SpinEdit';
    const translatedLabel = t(`attr.${attr.AttributeId}`, attr.Label);

    return (
      <div className="flex items-center mb-1.5" data-testid={`attr-field-${attr.AttributeId}`}>
        <label className={`w-32 text-xs ${labelColor} shrink-0`}>{translatedLabel}</label>
        {isSelect ? (
          <select value={val} onChange={e => setAttr(attr.AttributeId, e.target.value)}
            className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
            data-testid={`attr-select-${attr.AttributeId}`}>
            <option value="">{aeLoading ? 'Loading...' : '--Select--'}</option>
            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ) : (
          <input type={isDate ? 'date' : isNumber ? 'number' : 'text'} value={val}
            onChange={e => setAttr(attr.AttributeId, e.target.value)}
            className={`flex-1 px-2 py-1 text-xs rounded border ${borderColor} ${inputBg} ${textColor}`}
            data-testid={`attr-input-${attr.AttributeId}`} />
        )}
      </div>
    );
  };

  /** Render a group of AE attributes with a sub-heading */
  const AttrGroup = ({ groupId }) => {
    const attrs = getGroup(groupId);
    if (!attrs.length) return null;
    return (
      <div className="mb-3">
        <div className={`text-xs font-semibold uppercase tracking-wider mb-1.5 pb-1 border-b ${borderColor} ${subHeadColor}`}>
          {GROUP_LABELS[groupId] || groupId}
        </div>
        <div className="grid grid-cols-2 gap-x-4">
          {attrs.map(attr => <AttrField key={attr.AttributeId} attr={attr} />)}
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'basic-info',     label: t('itemMaster.tab_basic', 'Basic Info') },
    { id: 'classification', label: t('itemMaster.tab_classification', 'Classification') },
    { id: 'price-info',     label: t('itemMaster.tab_price', 'Price Info') },
    { id: 'tax-info',       label: t('itemMaster.tab_tax', 'Tax Info') },
    { id: 'stock-info',     label: t('itemMaster.tab_stock', 'Stock Info') },
    { id: 'images',         label: t('itemMaster.tab_images', 'Images') },
  ];

  return (
    <div className={`h-full flex flex-col ${bgColor} ${textColor} p-1 overflow-auto`} data-testid="item-master-screen">

      {/* Header */}
      <div className={`${cardBg} rounded-lg p-1.5 mb-1 border ${borderColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">{t('itemMaster.title', 'Item Master')}</h1>
            <p className="text-xs text-gray-400">Screen ID: 4.1.2 | Procurement Module</p>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher theme={theme} data-testid="im-lang-switcher" />
            <button onClick={loadData} className="p-2 rounded bg-blue-600 hover:bg-blue-700 text-white" data-testid="refresh-btn">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={resetForm} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs" data-testid="add-btn">
              <Plus size={14} /> Add New
            </button>
            <button onClick={handleSave} className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="save-btn">
              <Save size={14} /> {t('common.save', 'Save')}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-1 p-2 bg-red-500/20 border border-red-500/50 rounded flex items-center gap-2">
          <AlertCircle className="text-red-400" size={16} />
          <span className="text-red-400 text-xs">{error}</span>
          <button onClick={() => setError(null)} className="ml-auto text-red-400 text-sm">×</button>
        </div>
      )}

      {/* Tabs */}
      <div className={`${cardBg} rounded-lg border ${borderColor}`}>
        <div className={`flex border-b ${borderColor} overflow-x-auto`}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-xs font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.id ? 'border-blue-500 text-blue-400' : `border-transparent ${labelColor} hover:text-white`
              }`}>
              {tab.label}
            </button>
          ))}
        </div>

        <div className="p-3">

          {/* Basic Info */}
          {activeTab === 'basic-info' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`}>
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor}`}>Basic Information</div>
              <div className="grid grid-cols-2 gap-x-6">
                <div>
                  <InputField label={t('itemMaster.item_code', 'Item Code')} name="itemCode" disabled />
                  <InputField label={t('itemMaster.barcode', 'Barcode')} name="barcode" />
                  <InputField label={t('itemMaster.item_name', 'Item Name')} name="itemName" required />
                  <InputField label={t('itemMaster.short_name', 'Short Name')} name="shortName" />
                </div>
                <div>
                  <InputField label={t('itemMaster.hsn_code', 'HSN Code')} name="hsnCode" />
                  <StaticSelect label={t('itemMaster.status', 'Status')} name="status" options={['Active', 'Inactive', 'Discontinued']} />
                  <div className="flex items-center mb-1.5">
                    <label className={`w-32 text-xs ${labelColor} shrink-0`}>{t('itemMaster.allow_discount', 'Allow Discount')}</label>
                    <input type="checkbox" checked={!!formData.allowDiscount} onChange={e => setField('allowDiscount', e.target.checked)} className="w-4 h-4" data-testid="input-allowDiscount" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Classification — FULLY DYNAMIC from Attribute Engine, industry-filtered */}
          {activeTab === 'classification' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`} data-testid="classification-tab">
              {/* Industry selector — shared with Item Creation */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid var(--erp-border, #e5e7eb)` }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--erp-text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Industry</span>
                <select style={{ fontSize: 11, flex: '0 0 200px', padding: '3px 8px', border: '1px solid #d1d5db', borderRadius: 4, background: 'white' }}
                  value={selectedIndustry} onChange={e => handleIndustryChange(e.target.value)}
                  data-testid="item-master-industry-selector">
                  <option value="">— All —</option>
                  {aeIndustries.map(i => <option key={i.IndustryId} value={i.IndustryId}>{i.Icon} {i.IndustryName}</option>)}
                </select>
                <span style={{ fontSize: 10, color: '#6b7280' }}>
                  {selectedIndustry ? `Showing fields for ${selectedIndustry}` : 'Showing all attributes'}
                </span>
              </div>
              {aeLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-400 py-4 justify-center">
                  <RefreshCw size={14} className="animate-spin" /> Loading attributes from Attribute Studio...
                </div>
              ) : (
                <>
                  {/* Render industry-filtered groups */}
                  {(industryGroups.length > 0 ? industryGroups : CLASSIFICATION_GROUPS.map(gid => ({ GroupId: gid, Label: GROUP_LABELS[gid] || gid }))).map(group => {
                    const attrs = industryByGroup[group.GroupId] || [];
                    if (!attrs.length) return null;
                    return (
                      <div key={group.GroupId} className={`mb-3`}>
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor} ${subHeadColor}`}>
                          {group.Icon && <span style={{ marginRight: 4 }}>{group.Icon}</span>}
                          {group.Label}
                        </div>
                        <div className="grid grid-cols-2 gap-x-4">
                          {attrs.map(attr => <AttrField key={attr.AttributeId} attr={attr} />)}
                        </div>
                      </div>
                    );
                  })}
                  {Object.keys(industryByGroup).filter(k => k !== '(none)').length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">
                      No classification attributes configured in Attribute Studio for this industry.
                      <br />Go to <strong>Attribute Studio › Industry Config</strong> to configure.
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Price Info — grp_pricing from AE */}
          {activeTab === 'price-info' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`}>
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor}`}>Price Information</div>
              <div className="grid grid-cols-2 gap-x-6">
                <div>
                  <InputField label={t('itemMaster.cost_price', 'Cost Price')} name="costPrice" type="number" />
                  <InputField label={t('attr.mrp', 'MRP')} name="mrp" type="number" required />
                  <InputField label={t('itemMaster.selling_price', 'Selling Price')} name="sellingPrice" type="number" />
                </div>
                <div>
                  <InputField label={t('itemMaster.min_selling', 'Min Sell Price')} name="minSellingPrice" type="number" />
                  <InputField label={t('itemMaster.margin', 'Margin %')} name="marginPercent" type="number" />
                </div>
              </div>
              {/* AE pricing attrs (wholesale, WSP, discount type/val) */}
              {getGroup('grp_pricing').length > 0 && (
                <div className="mt-3">
                  <div className={`text-xs font-semibold uppercase tracking-wider mb-1.5 pb-1 border-b ${borderColor} ${subHeadColor}`}>
                    Extended Pricing (Attribute Studio)
                  </div>
                  <div className="grid grid-cols-2 gap-x-4">
                    {getGroup('grp_pricing')
                      .filter(a => !['mrp'].includes(a.AttributeId))
                      .map(attr => <AttrField key={attr.AttributeId} attr={attr} />)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tax Info */}
          {activeTab === 'tax-info' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`}>
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor}`}>Tax Information</div>
              <div className="max-w-md">
                <InputField label="Tax ID" name="taxId" />
                <InputField label="Tax %" name="taxPercent" type="number" />
                <InputField label="HSN/SAC Code" name="hsnSacCode" />
              </div>
            </div>
          )}

          {/* Stock / Compliance — grp_compliance from AE */}
          {activeTab === 'stock-info' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`}>
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor}`}>Stock Information</div>
              <div className="max-w-md">
                <InputField label={t('itemMaster.uom', 'UOM')} name="uomId" />
                <InputField label={t('itemMaster.reorder_level', 'Reorder Level')} name="reorderLevel" type="number" />
                <InputField label={t('itemMaster.min_order_qty', 'Min Order Qty')} name="minOrderQty" type="number" />
              </div>
              {getGroup('grp_compliance').length > 0 && (
                <div className="mt-3">
                  <div className={`text-xs font-semibold uppercase tracking-wider mb-1.5 pb-1 border-b ${borderColor} ${subHeadColor}`}>
                    Compliance Fields (Attribute Studio)
                  </div>
                  <div className="max-w-md">
                    {getGroup('grp_compliance')
                      .filter(a => !['uom', 'hsn'].includes(a.AttributeId))
                      .map(attr => <AttrField key={attr.AttributeId} attr={attr} />)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Images */}
          {activeTab === 'images' && (
            <div className={`${sectionBg} rounded p-3 border ${borderColor}`}>
              <div className={`text-xs font-semibold uppercase tracking-wider mb-2 pb-1 border-b ${borderColor}`}>Product Images</div>
              <div className="grid grid-cols-4 gap-2">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className={`aspect-square border-2 border-dashed ${borderColor} rounded flex items-center justify-center ${inputBg}`}>
                    <div className="text-center">
                      <Image size={28} className="mx-auto text-gray-400 mb-1" />
                      <label className="text-xs text-blue-400 cursor-pointer hover:underline">
                        Upload {i}<input type="file" accept="image/*" className="hidden" />
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Item List */}
      <div className={`${cardBg} rounded-lg border ${borderColor} mt-1 overflow-hidden flex-shrink-0`}>
        <div className={`px-2 py-1 border-b ${borderColor} flex items-center justify-between`}>
          <h3 className="text-sm font-semibold">Item List</h3>
          <span className="text-xs text-gray-400">{data.length} items</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="data-table">
            <thead className={isDark ? 'bg-[#0f0f1a]' : 'bg-gray-100'}>
              <tr>
                {['Sl', 'Item Code', 'Item Name', 'HSN', 'Division', 'Category', 'MRP', 'Status', 'Actions'].map(h => (
                  <th key={h} className={`px-3 py-2 text-${h === 'MRP' ? 'right' : h === 'Actions' ? 'center' : 'left'} font-semibold text-xs uppercase tracking-wide`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((item, idx) => (
                <tr key={item.id} className={`border-t ${borderColor} hover:${isDark ? 'bg-[#0f0f1a]' : 'bg-gray-50'}`}>
                  <td className="px-3 py-1.5">{idx + 1}</td>
                  <td className="px-3 py-1.5 font-mono">{item.itemCode}</td>
                  <td className="px-3 py-1.5 font-medium">{item.itemName}</td>
                  <td className="px-3 py-1.5">{item.hsnCode}</td>
                  <td className="px-3 py-1.5">{item.division}</td>
                  <td className="px-3 py-1.5">{item.category}</td>
                  <td className="px-3 py-1.5 text-right">{item.mrp}</td>
                  <td className="px-3 py-1.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${item.status === 'Active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>{item.status}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => handleEdit(item)} className="p-1 rounded bg-blue-500/20 text-blue-400 hover:bg-blue-500/30" data-testid={`edit-${item.id}`}><Edit2 size={11} /></button>
                      <button onClick={() => handleDelete(item)} className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30" data-testid={`delete-${item.id}`}><Trash2 size={11} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default ItemMasterScreen;
